import React, { useState } from 'react';
import { Button, Card, ErrorMessage, FlexRow, FormCell, Grid, Header2Text } from 'ui-toolkit';
import { IFetchStatus } from 'types/rest/IFetchStatus';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserPlus } from '@fortawesome/pro-regular-svg-icons';
import { AddClientContact } from '../../components/clients/AddClientContact';
import { ClientUsersTable, IUserAction } from '../../components/clients/ClientUsersTable';
import { EditClientContact } from '../../components/clients/EditClientContact';
import { eClientContactActionType } from '../../types/contacts/eClientContactActionType';
import { IClientUserData } from '../../types/clients';
import { DeactivateClientUser } from 'components/clients/DeactivateClientUser';
import { ActivateClientUser } from 'components/clients/ActivateClientUser';
import { useListContactFunctionsQuery } from '../../API/contacts/ContactFunctionsApi';
import { Show } from '../../components/shared/Show';

interface IClientUsersPageProps {
  clientCompanyId: string;
  pageError?: Error | null;
  clientContacts: IClientUserData[];
  onAddContact: (data: IClientUserData) => void;
  onAddContactReset: () => void;
  addContactFetchStatus: IFetchStatus;
  getContactFetchStatus: IFetchStatus;
  addContactError?: Error;
  onEditContact: (data: IClientUserData) => void;
  onEditContactReset: () => void;
  editContactFetchStatus: IFetchStatus;
  editContactError?: Error;
}

export const ClientUsersPage: React.FC<IClientUsersPageProps> = ({
  clientCompanyId,
  pageError,
  clientContacts,
  onAddContact,
  onAddContactReset,
  addContactFetchStatus,
  getContactFetchStatus,
  addContactError,
  onEditContact,
  onEditContactReset,
  editContactFetchStatus,
  editContactError,
}) => {
  const [showAddForm, setShowForm] = useState(false);
  const [editContact, setEditContact] = useState<IClientUserData | null>(null);
  const [contactAction, setContactAction] = useState<IUserAction | null>(null);
  const { data: contactFunctions = [] } = useListContactFunctionsQuery();

  const onResetAddContact = () => {
    onAddContactReset();
    setShowForm(false);
  };
  const onResetEditContact = () => {
    onEditContactReset();
    setEditContact(null);
  };

  const onInputEmail = (email: string) => {
    const clientContact = clientContacts.find((c) => c.email === email);
    if (clientContact) {
      setShowForm(false);
      setEditContact(clientContact);
    }
  };

  return (
    <Grid>
      <ErrorMessage error={pageError} />
      <Card>
        <Grid>
          <FlexRow justifyContent={'space-between'} width={100}>
            <FormCell hintText={'Points of contact for the client.'}>
              <Header2Text>Client Contacts</Header2Text>
            </FormCell>
            <Button intent={'secondary'} onClick={() => setShowForm(true)}>
              <FontAwesomeIcon icon={faUserPlus} /> Add New Contact
            </Button>
          </FlexRow>
          <Show when={contactFunctions.length !== 0}>
            <ClientUsersTable
              data={clientContacts}
              loading={getContactFetchStatus.fetching}
              onEditContact={setEditContact}
              setContactAction={setContactAction}
              contactFunctions={contactFunctions}
            />
          </Show>
        </Grid>
      </Card>
      {showAddForm && (
        <AddClientContact
          onSubmit={onAddContact}
          addFetchStatus={addContactFetchStatus}
          addFetchError={addContactError}
          onCancel={onResetAddContact}
          onAddContact={onInputEmail}
        />
      )}
      {editContact && (
        <EditClientContact
          onSubmit={onEditContact}
          editFetchStatus={editContactFetchStatus}
          editFetchError={editContactError}
          onCancel={onResetEditContact}
          initialData={editContact}
        />
      )}
      {contactAction?.type === eClientContactActionType.Deactivate && (
        <DeactivateClientUser
          done={() => setContactAction(null)}
          user={contactAction.user}
          clientCompanyId={clientCompanyId}
        />
      )}
      {contactAction?.type === eClientContactActionType.Activate && (
        <ActivateClientUser
          done={() => setContactAction(null)}
          user={contactAction.user}
          clientCompanyId={clientCompanyId}
        />
      )}
    </Grid>
  );
};
